import React, { useEffect } from 'react';

import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogFooter,
  Form,
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  SocialLinks,
  UpdateUserDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import {
  facebookBaseUrl,
  gitHubBaseUrl,
  httpsScheme,
  linkedInBaseUrl,
  tikTokBaseUrl,
  twitterBaseUrl,
  youTubeBaseUrl,
} from '../../social';

/**
 * Each field stores a handle/path appended to a fixed base URL (see
 * `startAdornment` below), not a full URL - so `.url()` would be the wrong
 * check here. Broad on purpose: real handles carry dots, underscores and
 * multi-segment paths (a LinkedIn company page, a GitHub org/repo). What
 * actually breaks the link is pasting the full URL by mistake (the base
 * would then appear twice), which this rejects explicitly, or whitespace.
 */
const socialHandle = () =>
  z
    .string()
    .trim()
    .max(100, 'Must be 100 characters or fewer')
    .refine(
      (value) => !/^(https?:\/\/|www\.)/i.test(value),
      "Enter just your username, not the full link - it's added automatically"
    )
    .refine((value) => !/\s/.test(value), 'Spaces are not allowed')
    .optional();

const formSchema = z.object({
  facebookUrl: socialHandle(),
  twitterUrl: socialHandle(),
  tikTokUrl: socialHandle(),
  youtubeUrl: socialHandle(),
  githubUrl: socialHandle(),
  linkedInUrl: socialHandle(),
});

export const SocialProfilesSectionEditDialog = ({
  defaultValues,
  open,
  handleOpenChange,
}: {
  defaultValues?: SocialLinks;
  open: boolean;
  handleOpenChange: (open: boolean) => void;
}) => {
  const [updateUser, { loading: updateUserLoading }] =
    useMutation(UpdateUserDocument);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    form.setValue('facebookUrl', defaultValues?.facebook || '');
    form.setValue('twitterUrl', defaultValues?.twitter || '');
    form.setValue('tikTokUrl', defaultValues?.tikTok || '');
    form.setValue('youtubeUrl', defaultValues?.youtube || '');
    form.setValue('githubUrl', defaultValues?.github || '');
    form.setValue('linkedInUrl', defaultValues?.linkedIn || '');
  }, [defaultValues, form]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await updateUser({
      variables: {
        input: {
          facebookURL: values.facebookUrl,
          twitterUrl: values.twitterUrl,
          tikTokUrl: values.tikTokUrl,
          youtubeUrl: values.youtubeUrl,
          githubUrl: values.githubUrl,
          linkedInURL: values.linkedInUrl,
        },
      },
    });

    handleOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      className="bg-card"
      title="Social Profiles"
      description="Add your social links"
    >
      <Form {...form}>
        <form
          className="flex flex-col justify-between"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name="linkedInUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LinkedIn</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + linkedInBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facebookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facebook</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + facebookBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="twitterUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>X (Twitter)</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + twitterBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="youtubeUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Youtube</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + youTubeBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tikTokUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TikTok</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + tikTokBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="githubUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GitHub</FormLabel>
                  <FormControl>
                    <Input
                      startAdornment={httpsScheme + gitHubBaseUrl}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-1">
            <Button
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                form.reset();
                handleOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateUserLoading}>
              {updateUserLoading ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </Dialog>
  );
};
